import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';
import ConfirmDialog from '../components/ConfirmDialog';
import { isPro } from '../Helpers';
import { useModalManager } from '../hooks/useModalManager';
import PriceModal from '../components/PriceModal/PriceModal';

const Customers = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const { modalConfig, closeModal, showPremiumModal } = useModalManager();

    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [settings, setSettings] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeTab, setActiveTab] = useState('customers');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);


    // Auto-search with debouncing
    useEffect(() => {
        if (activeTab === 'customers') {
            const timer = setTimeout(() => {
                loadCustomers();
            }, 300); // 300ms debounce

            return () => clearTimeout(timer);
        }
    }, [searchTerm, currentPage, roleFilter, activeTab]);

    const loadSettings = async () => {
        try {
            const response = await api.getSettings();
            if (response.success && response.settings) {
                setSettings(response.settings);
                console.log('👥 Customers - Settings loaded:', response.settings);
            }
        } catch (err) {
            console.error('Failed to load settings:', err);
        }
    };

    const handleExport = async () => {
        // Check if user has premium access for export CSV feature
        if (!isPro()) {
            showPremiumModal();
            return;
        }

        try {
            showSuccess('Customer export functionality coming soon!');
        } catch (err) {
            showError('Export failed: ' + err.message);
        }
    };

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/customers', {
                page: currentPage,
                per_page: 20,
                search: searchTerm,
                role: roleFilter
            });

            if (response.success) {
                setCustomers(response.customers || []);
                setTotalPages(response.total_pages || 1);
            }
        } catch (err) {
            console.error('Load customers error:', err);
            showError('Failed to load customers: ' + err.message);
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setRoleFilter('all');
        setCurrentPage(1);
    };

    const handleDelete = (customer) => {
        setCustomerToDelete(customer);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!customerToDelete) return;

        try {
            const response = await api.delete(`/customers/${customerToDelete.id}`);
            if (response.success) {
                showSuccess('Customer deleted successfully');
                loadCustomers();
            } else {
                throw new Error(response.message || 'Failed to delete customer');
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setDeleteModalOpen(false);
            setCustomerToDelete(null);
        }
    };

    const handleView = async (customer) => {
        try {
            // Load full customer details
            const response = await api.get(`/customers/${customer.id}`);
            if (response.success) {
                // Add avatar_url from the list data if not present
                const customerData = response.customer;
                if (!customerData.avatar_url && customer.avatar_url) {
                    customerData.avatar_url = customer.avatar_url;
                }
                setViewingCustomer(customerData);
                setViewModalOpen(true);
            }
        } catch (err) {
            showError('Failed to load customer details: ' + err.message);
        }
    };

    // Customer List View
    return (
        <div className="page customers-page">
            <div className="page-container">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h1>Customers</h1>
                        <p>Manage your WooCommerce customers</p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="tab-navigation">
                    <button
                        className={`tab-button ${activeTab === 'customers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('customers')}
                    >
                        👥 Customers List
                    </button>

                </div>

                {activeTab === 'customers' && (
                    <div className="customers-container">
                        <div className="page-header">
                            <div className="page-actions">
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => navigate('/customers/add')}
                                >
                                    + Add Customer
                                </button>

                                {settings.sync_customers && (
                                    <button
                                        className={`btn btn-secondary btn-sm ${!isPro() ? 'premium-disabled' : ''}`}
                                        onClick={handleExport}
                                        title={!isPro() ? 'Premium Feature - Upgrade to unlock' : 'Export customers to CSV'}
                                    >
                                        📥 Export CSV {!isPro() ? '🔒' : ''}
                                    </button>
                                )}

                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={loadCustomers}
                                    disabled={loading}
                                >
                                    🔄 Refresh
                                </button>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="filters-card">
                            <div className="filters-row">
                                <div className="filter-group">
                                    <input
                                        type="text"
                                        className="filter-input"
                                        placeholder="🔍 Search by name, email, username..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                <div className="filter-group">
                                    <select
                                        className="filter-select"
                                        value={roleFilter}
                                        onChange={(e) => {
                                            setRoleFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">All Roles</option>
                                        <option value="customer">Customer</option>
                                        <option value="shop_manager">Shop Manager</option>
                                        <option value="subscriber">Subscriber</option>
                                        <option value="contributor">Contributor</option>
                                        <option value="author">Author</option>
                                        <option value="editor">Editor</option>
                                        <option value="administrator">Administrator</option>
                                    </select>
                                </div>
                                {(searchTerm || roleFilter !== 'all') && (
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleClearFilters}
                                        title="Clear all filters"
                                    >
                                        ✕ Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Customers Table */}
                        <div className="data-table-card">
                            {loading ? (
                                <LoadingState message="Loading customers..." />
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Customer</th>
                                                    <th>Email</th>
                                                    <th>Role</th>
                                                    <th>Orders</th>
                                                    <th>Total Spent</th>
                                                    <th>Registered</th>
                                                    <th className="actions-column">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customers.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="empty-state">
                                                            <div className="empty-icon">👥</div>
                                                            <p>No customers found</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    customers.map(customer => (
                                                        <tr key={customer.id}>
                                                            <td>
                                                                <div className="customer-cell">
                                                                    {customer.avatar_url ? (
                                                                        <img
                                                                            src={customer.avatar_url}
                                                                            alt={customer.username}
                                                                            className="customer-avatar-img"
                                                                        />
                                                                    ) : (
                                                                        <div className="customer-avatar">
                                                                            {(customer.first_name?.charAt(0) || customer.username?.charAt(0) || 'U').toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                    <div className="customer-info">
                                                                        <div className="customer-name">
                                                                            {customer.first_name} {customer.last_name}
                                                                        </div>
                                                                        <div className="customer-username">@{customer.username}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{customer.email}</td>
                                                            <td>
                                                                <span className="role-badge">{customer.role}</span>
                                                            </td>
                                                            <td>{customer.orders_count || 0}</td>
                                                            <td>${customer.total_spent || '0.00'}</td>
                                                            <td>{new Date(customer.date_created).toLocaleDateString()}</td>
                                                            <td className="actions-column">
                                                                <div className="action-buttons">
                                                                    <button
                                                                        className="btn-icon"
                                                                        onClick={() => handleView(customer)}
                                                                        title="View"
                                                                    >
                                                                        👁️
                                                                    </button>
                                                                    <button
                                                                        className="btn-icon"
                                                                        onClick={() => navigate(`/customers/edit/${customer.id}`)}
                                                                        title="Edit"
                                                                    >
                                                                        ✏️
                                                                    </button>
                                                                    <button
                                                                        className="btn-icon btn-danger"
                                                                        onClick={() => handleDelete(customer)}
                                                                        title="Delete"
                                                                    >
                                                                        🗑️
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="pagination">
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                            >
                                                Previous
                                            </button>
                                            <span className="pagination-info">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Customer View Modal - CV Style */}
                {viewModalOpen && viewingCustomer && (
                    <div className="modal-overlay" onClick={() => setViewModalOpen(false)}>
                        <div className="modal-content customer-cv-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Customer Profile</h3>
                                <button className="modal-close" onClick={() => setViewModalOpen(false)}>
                                    ✕
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="customer-cv">
                                    {/* Header Section */}
                                    <div className="cv-header">
                                        {viewingCustomer.avatar_url ? (
                                            <img
                                                src={viewingCustomer.avatar_url}
                                                alt={viewingCustomer.username}
                                                className="cv-avatar-img"
                                            />
                                        ) : (
                                            <div className="cv-avatar">
                                                {(viewingCustomer.first_name?.charAt(0) || viewingCustomer.username?.charAt(0) || 'U').toUpperCase()}
                                            </div>
                                        )}
                                        <div className="cv-header-info">
                                            <h2>{viewingCustomer.first_name} {viewingCustomer.last_name}</h2>
                                            <p className="cv-username">@{viewingCustomer.username}</p>
                                            <div className="cv-badges">
                                                <span className="cv-badge role">{viewingCustomer.role}</span>
                                                <span className="cv-badge">ID: {viewingCustomer.id}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact Information */}
                                    <div className="cv-section">
                                        <h3 className="cv-section-title">📧 Contact Information</h3>
                                        <div className="cv-grid">
                                            <div className="cv-item">
                                                <span className="cv-label">Email</span>
                                                <span className="cv-value">{viewingCustomer.email}</span>
                                            </div>
                                            {viewingCustomer.billing?.phone && (
                                                <div className="cv-item">
                                                    <span className="cv-label">Phone</span>
                                                    <span className="cv-value">{viewingCustomer.billing.phone}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Account Statistics */}
                                    <div className="cv-section">
                                        <h3 className="cv-section-title">📊 Account Statistics</h3>
                                        <div className="cv-stats">
                                            <div className="cv-stat-card">
                                                <div className="cv-stat-icon">🛒</div>
                                                <div className="cv-stat-info">
                                                    <div className="cv-stat-value">{viewingCustomer.orders_count || 0}</div>
                                                    <div className="cv-stat-label">Total Orders</div>
                                                </div>
                                            </div>
                                            <div className="cv-stat-card">
                                                <div className="cv-stat-icon">💰</div>
                                                <div className="cv-stat-info">
                                                    <div className="cv-stat-value">${viewingCustomer.total_spent || '0.00'}</div>
                                                    <div className="cv-stat-label">Total Spent</div>
                                                </div>
                                            </div>
                                            <div className="cv-stat-card">
                                                <div className="cv-stat-icon">📅</div>
                                                <div className="cv-stat-info">
                                                    <div className="cv-stat-value">
                                                        {new Date(viewingCustomer.date_created).toLocaleDateString()}
                                                    </div>
                                                    <div className="cv-stat-label">Member Since</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Billing Address */}
                                    {viewingCustomer.billing && (
                                        <div className="cv-section">
                                            <h3 className="cv-section-title">🏠 Billing Address</h3>
                                            <div className="cv-address">
                                                {viewingCustomer.billing.company && (
                                                    <p><strong>{viewingCustomer.billing.company}</strong></p>
                                                )}
                                                <p>{viewingCustomer.billing.first_name} {viewingCustomer.billing.last_name}</p>
                                                {viewingCustomer.billing.address_1 && (
                                                    <>
                                                        <p>{viewingCustomer.billing.address_1}</p>
                                                        {viewingCustomer.billing.address_2 && <p>{viewingCustomer.billing.address_2}</p>}
                                                        <p>
                                                            {viewingCustomer.billing.city}
                                                            {viewingCustomer.billing.state && `, ${viewingCustomer.billing.state}`}
                                                            {viewingCustomer.billing.postcode && ` ${viewingCustomer.billing.postcode}`}
                                                        </p>
                                                        <p>{viewingCustomer.billing.country}</p>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Shipping Address */}
                                    {viewingCustomer.shipping && viewingCustomer.shipping.address_1 && (
                                        <div className="cv-section">
                                            <h3 className="cv-section-title">📦 Shipping Address</h3>
                                            <div className="cv-address">
                                                {viewingCustomer.shipping.company && (
                                                    <p><strong>{viewingCustomer.shipping.company}</strong></p>
                                                )}
                                                <p>{viewingCustomer.shipping.first_name} {viewingCustomer.shipping.last_name}</p>
                                                <p>{viewingCustomer.shipping.address_1}</p>
                                                {viewingCustomer.shipping.address_2 && <p>{viewingCustomer.shipping.address_2}</p>}
                                                <p>
                                                    {viewingCustomer.shipping.city}
                                                    {viewingCustomer.shipping.state && `, ${viewingCustomer.shipping.state}`}
                                                    {viewingCustomer.shipping.postcode && ` ${viewingCustomer.shipping.postcode}`}
                                                </p>
                                                <p>{viewingCustomer.shipping.country}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setViewModalOpen(false);
                                        navigate(`/customers/edit/${viewingCustomer.id}`);
                                    }}
                                >
                                    ✏️ Edit Customer
                                </button>
                                <button className="btn btn-secondary" onClick={() => setViewModalOpen(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <ConfirmDialog
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onConfirm={confirmDelete}
                    title="Confirm Delete"
                    confirmText="Delete Customer"
                    confirmButtonClass="btn-danger"
                >
                    <p>Are you sure you want to delete customer <strong>{customerToDelete?.display_name || customerToDelete?.username}</strong>?</p>
                    <p className="text-danger">This action cannot be undone.</p>
                </ConfirmDialog>

                {/* Premium Modal */}
                <PriceModal
                    isOpen={modalConfig.isOpen}
                    onClose={closeModal}
                    onConfirm={modalConfig.onConfirm}
                    onDecline={closeModal}
                    type={modalConfig.type}
                    title={modalConfig.title}
                    message={modalConfig.message}
                    confirmText={modalConfig.confirmText}
                    declineText={modalConfig.declineText}
                    position={modalConfig.position}
                />

            </div>
        </div>
    );
};

export default Customers;
